// ─────────────────────────────────────────────────────────────────────────────
// KnowledgeBasePageComponent
// ─────────────────────────────────────────────────────────────────────────────
// Main page for the Knowledge Base feature.
// Responsibilities:
//   - Display page header with action buttons
//   - Manage two-level tab navigation
//     (Documents | Products > Ext Refs | Alias | Locations | Pricing > Observations | Imports)
//   - Persist active tab state via URL query params
//   - Host child tab content components
// ─────────────────────────────────────────────────────────────────────────────

import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';

import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { PageHeaderComponent } from 'src/app/ui/page-header/page-header.component';
import { UiModule } from 'src/app/ui/ui.module';
import { SharedModule } from 'src/app/shared/shared.module';
import { DocumentsTabComponent } from '../documents-tab/documents-tab.component';
import { ProductsTabComponent } from '../products-tab/products-tab.component';
import { LocationsTabComponent } from '../locations-tab/locations-tab.component';
import { PricingTabComponent } from '../pricing-tab/pricing-tab.component';

type KnowledgeBaseL1Tab = 'documents' | 'products' | 'locations' | 'pricing';

@Component({
  selector: 'app-knowledge-base-page',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    PageHeaderComponent,
    UiModule,
    SharedModule,
    DocumentsTabComponent,
    ProductsTabComponent,
    LocationsTabComponent,
    PricingTabComponent,
  ],
  templateUrl: './knowledge-base-page.component.html',
  styleUrls: ['./knowledge-base-page.component.scss'],
})
export class KnowledgeBasePageComponent implements OnInit, OnDestroy {
  // ── Tab state ──────────────────────────────────────────────────────────

  /** Products is the landing tab — Documents/Locations/Pricing are opened explicitly. */
  activeL1Tab: KnowledgeBaseL1Tab = 'products';

  /** Pricing is the wolsal_pricing addon. */
  pricingEnabled = false;

  /** Kept apart: the flag and the query params arrive in either order. */
  private requestedTab: KnowledgeBaseL1Tab = 'products';

  // ── Cleanup ────────────────────────────────────────────────────────────

  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private featureFlagService: FeatureFlagService,
  ) {}

  ngOnInit(): void {
    this.featureFlagService.loadFeatures().pipe(takeUntil(this.destroy$)).subscribe();

    this.featureFlagService.pricingEnabled$
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled) => {
        this.pricingEnabled = enabled;
        this.applyRequestedTab();
      });

    this.activatedRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const tab = params['tab'];
      this.requestedTab =
        tab === 'documents' || tab === 'locations' || tab === 'pricing' ? tab : 'products';
      this.applyRequestedTab();
    });
  }

  private applyRequestedTab(): void {
    this.activeL1Tab =
      this.requestedTab === 'pricing' && !this.pricingEnabled ? 'products' : this.requestedTab;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Tab navigation ─────────────────────────────────────────────────────

  onL1TabChange(tab: KnowledgeBaseL1Tab): void {
    this.activeL1Tab = tab;
    const queryParams: Record<string, string | null> = { tab };
    // Products and Pricing carry their own subtab; Documents and Locations have none.
    if (tab === 'documents' || tab === 'locations') {
      queryParams['subtab'] = null;
    }
    this.router.navigate([], { queryParams, queryParamsHandling: 'merge' });
  }
}
